import { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageSquare, Check, X, Trash2, Filter, BadgeCheck, Star, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadReviews();
  }, [filter]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const statusParam = filter !== 'all' ? `?status_filter=${filter}` : '';
      const response = await axios.get(`${API}/admin/reviews${statusParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data.reviews);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error loading reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (reviewId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/reviews/${reviewId}/status?new_status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Review ${newStatus}`);
      loadReviews();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review deleted');
      loadReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div data-testid="admin-reviews-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">
            <span className="text-white">MANAGE</span>{' '}
            <span className="text-[#D4AF37]">REVIEWS</span>
          </h1>
          <p className="text-[#A1A1AA] text-sm mt-1">
            Approve, reject, or delete customer reviews
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div 
          className={`glass p-4 cursor-pointer transition-all ${filter === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setFilter('pending')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A1A1AA] text-sm">Pending</p>
              <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        <div 
          className={`glass p-4 cursor-pointer transition-all ${filter === 'approved' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setFilter('approved')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A1A1AA] text-sm">Approved</p>
              <p className="text-2xl font-bold text-green-500">{stats.approved}</p>
            </div>
            <Check className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div 
          className={`glass p-4 cursor-pointer transition-all ${filter === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setFilter('rejected')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A1A1AA] text-sm">Rejected</p>
              <p className="text-2xl font-bold text-red-500">{stats.rejected}</p>
            </div>
            <X className="w-8 h-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-4 h-4 text-[#A1A1AA]" />
        <span className="text-sm text-[#A1A1AA]">Filter:</span>
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                filter === status
                  ? 'bg-[#D4AF37] text-black font-medium'
                  : 'bg-[#2A2A2A] text-[#A1A1AA] hover:bg-[#3A3A3A]'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="glass p-4 animate-pulse">
              <div className="h-4 bg-[#2A2A2A] rounded w-1/4 mb-2" />
              <div className="h-4 bg-[#2A2A2A] rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <div className="glass p-12 text-center">
          <MessageSquare className="w-16 h-16 text-[#D4AF37] mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-semibold text-white mb-2">No Reviews Found</h3>
          <p className="text-[#A1A1AA]">
            {filter !== 'all' 
              ? `No ${filter} reviews at the moment.`
              : 'No reviews have been submitted yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div 
              key={review.id} 
              className="glass p-5"
              data-testid={`review-item-${review.id}`}
            >
              {/* Review Header */}
              <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-white">{review.user_name}</span>
                    {review.is_verified && (
                      <span className="inline-flex items-center gap-1 bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full">
                        <BadgeCheck className="w-3 h-3" />
                        Verified
                      </span>
                    )}
                    {getStatusBadge(review.status)}
                  </div>
                  <p className="text-sm text-[#A1A1AA]">
                    {review.product_name} • {formatDate(review.created_at)}
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  {review.status !== 'approved' && (
                    <button
                      onClick={() => updateStatus(review.id, 'approved')}
                      className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                      title="Approve"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  {review.status !== 'rejected' && (
                    <button
                      onClick={() => updateStatus(review.id, 'rejected')}
                      className="p-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg transition-colors"
                      title="Reject"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => deleteReview(review.id)}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= review.rating
                        ? 'fill-[#D4AF37] text-[#D4AF37]'
                        : 'text-[#3A3A3A]'
                    }`}
                  />
                ))}
                <span className="text-sm text-[#A1A1AA] ml-2">{review.rating}/5</span>
              </div>

              {/* Title & Comment */}
              {review.title && (
                <h4 className="font-medium text-white mb-1">{review.title}</h4>
              )}
              <p className="text-[#E5E5E5]">{review.comment}</p>

              {/* Review Images */}
              {review.images && review.images.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {review.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`Review ${idx + 1}`}
                      className="w-16 h-16 object-cover rounded-lg border border-white/10"
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
